import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  Icon,
  Input,
  Metric,
  Table,
  type Column,
} from '@/components/primitives';
import { PageHeader } from '@/components/layout/PageHeader';
import { useViewport } from '@/lib/viewport';
import { useCurrentConfig } from '@/hooks/useConfig';
import type { ConfigKind, ConfigValue } from '@/domain';

const KIND_LABEL: Record<ConfigKind, string> = {
  MONTHLY_MULTIPLIER: 'Hệ số tháng',
  EVENT_MULTIPLIER: 'Hệ số sự kiện',
  UPH_NORM: 'Định mức UPH',
  CUTOFF_DAYS: 'Cut-off cấp',
};

const KIND_LINK: Record<ConfigKind, string> = {
  MONTHLY_MULTIPLIER: '/admin/config/thamso',
  EVENT_MULTIPLIER: '/admin/config/thamso',
  UPH_NORM: '/admin/config/dinhmuc',
  CUTOFF_DAYS: '/admin/config/thamso',
};

export function ConfigPage() {
  const { isMobile, vw } = useViewport();
  const navigate = useNavigate();
  const all = useCurrentConfig();
  const [q, setQ] = useState('');
  const [kindFilter, setKindFilter] = useState<'all' | ConfigKind>('all');

  const grouped = useMemo(() => {
    const groups: Record<ConfigKind, ConfigValue[]> = {
      MONTHLY_MULTIPLIER: [],
      EVENT_MULTIPLIER: [],
      UPH_NORM: [],
      CUTOFF_DAYS: [],
    };
    for (const v of all) groups[v.kind]?.push(v);
    return groups;
  }, [all]);

  const rows = useMemo(() => {
    let r = all;
    if (kindFilter !== 'all') r = r.filter((v) => v.kind === kindFilter);
    if (q) {
      const needle = q.toLowerCase();
      r = r.filter((v) => v.key.toLowerCase().includes(needle));
    }
    return r.slice().sort((a, b) => a.key.localeCompare(b.key));
  }, [all, kindFilter, q]);

  const cols: Column<ConfigValue>[] = [
    {
      header: 'Key',
      render: (r) => (
        <span style={{ fontFamily: 'var(--bx-font-mono)', fontSize: 12 }}>{r.key}</span>
      ),
    },
    {
      header: 'Loại',
      priority: 2,
      render: (r) => <Badge kind="INFO">{KIND_LABEL[r.kind]}</Badge>,
    },
    {
      header: 'Giá trị',
      align: 'right',
      mono: true,
      width: 120,
      render: (r) =>
        r.kind === 'UPH_NORM' || r.kind === 'CUTOFF_DAYS' ? r.value : '×' + r.value.toFixed(2),
    },
    {
      header: 'v',
      align: 'right',
      mono: true,
      width: 60,
      priority: 2,
      render: (r) => 'v' + r.version,
    },
    {
      header: 'Hiệu lực từ',
      priority: 3,
      render: (r) => (
        <span style={{ fontFamily: 'var(--bx-font-mono)', fontSize: 12 }}>{r.effective_from}</span>
      ),
    },
    {
      header: 'Bởi',
      priority: 3,
      render: (r) => (
        <span style={{ fontSize: 11.5, color: 'var(--bx-text-muted)' }}>{r.created_by}</span>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <PageHeader
        breadcrumb={['Cấu hình', 'Tham số vận hành']}
        title="Tham số vận hành"
        subtitle="Kho tham số versioned · mọi thay đổi đều ghi audit log"
        actions={
          <>
            <Button
              variant="outline"
              icon="calendar"
              onClick={() => navigate('/admin/config/thamso')}
            >
              Mùa vụ & sự kiện
            </Button>
            <Button
              variant="primary"
              icon="layers"
              onClick={() => navigate('/admin/config/dinhmuc')}
            >
              Định mức UPH
            </Button>
          </>
        }
      />
      <div style={{ flex: 1, padding: isMobile ? 14 : 20 }}>
        <div
          style={{
            display: 'grid',
            gap: 12,
            marginBottom: 16,
            gridTemplateColumns: vw < 720 ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
          }}
        >
          <Metric
            label="Hệ số tháng"
            value={grouped.MONTHLY_MULTIPLIER.length}
            sublabel="của 12 tháng"
          />
          <Metric
            label="Hệ số sự kiện"
            value={grouped.EVENT_MULTIPLIER.length}
            sublabel="event types"
          />
          <Metric
            label="UPH norms"
            value={grouped.UPH_NORM.length}
            sublabel="theo kho × KH × group × pk"
          />
          <Metric
            label="Tổng phiên bản hiện hành"
            value={all.length}
            sublabel="is_current = true"
          />
        </div>

        <Card padding={0}>
          <div
            style={{
              padding: '12px 14px',
              borderBottom: '1px solid var(--bx-border-muted)',
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ width: 280, minWidth: 200 }}>
              <Input
                icon="search"
                placeholder="Tìm theo key…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                size="sm"
              />
            </div>
            <div
              role="tablist"
              style={{
                display: 'inline-flex',
                gap: 4,
                padding: 4,
                background: 'var(--bx-surface-2)',
                borderRadius: 10,
              }}
            >
              {(
                [
                  { id: 'all', label: 'Tất cả', count: all.length },
                  {
                    id: 'MONTHLY_MULTIPLIER' as ConfigKind,
                    label: 'Tháng',
                    count: grouped.MONTHLY_MULTIPLIER.length,
                  },
                  {
                    id: 'EVENT_MULTIPLIER' as ConfigKind,
                    label: 'Sự kiện',
                    count: grouped.EVENT_MULTIPLIER.length,
                  },
                  {
                    id: 'UPH_NORM' as ConfigKind,
                    label: 'UPH',
                    count: grouped.UPH_NORM.length,
                  },
                ] as { id: 'all' | ConfigKind; label: string; count: number }[]
              ).map((t) => {
                const active = kindFilter === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setKindFilter(t.id)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      height: 26,
                      padding: '0 12px',
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer',
                      background: active ? '#fff' : 'transparent',
                      color: active ? 'var(--bx-text-dark)' : 'var(--bx-text-body)',
                      fontWeight: active ? 600 : 500,
                      fontSize: 12.5,
                      boxShadow: active ? 'var(--bx-shadow-pill)' : 'none',
                      fontFamily: 'var(--bx-font-sans)',
                    }}
                  >
                    {t.label}
                    <span
                      style={{
                        fontVariantNumeric: 'tabular-nums',
                        fontSize: 11,
                        color: active ? 'var(--bx-primary)' : 'var(--bx-text-muted)',
                      }}
                    >
                      {t.count}
                    </span>
                  </button>
                );
              })}
            </div>
            <div style={{ flex: 1 }} />
            <Button variant="ghost" size="sm" icon="filter">
              Bộ lọc khác
            </Button>
          </div>

          <Table
            columns={cols}
            rows={rows}
            getRowKey={(r) => r.id}
            onRowClick={(r) => navigate(KIND_LINK[r.kind])}
            emptyText="Không có config khớp bộ lọc"
            stickyHeader
          />
        </Card>

        <Card title="Quy ước" subtitle="Đọc kỹ trước khi sửa" style={{ marginTop: 16 }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: 16,
            }}
          >
            <Hint
              icon="layers"
              title="Immutable & versioned"
              body="Mọi thay đổi tạo dòng mới (version + 1). Bản cũ giữ lại để rollback và audit."
            />
            <Hint
              icon="shield"
              title="Quyền sửa"
              body="Chỉ ADMIN có quyền sửa config. Mọi thao tác đều ghi audit_log với actor."
            />
            <Hint
              icon="rocket"
              title="Hiệu lực ngay"
              body="Engine forecast tiếp theo (cron nightly hoặc manual run) sẽ dùng version mới."
            />
          </div>
        </Card>
      </div>
    </div>
  );
}

function Hint({ icon, title, body }: { icon: string; title: string; body: string }) {
  return (
    <div style={{ display: 'flex', gap: 12 }}>
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: '#eef2ff',
          color: 'var(--bx-primary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon name={icon} size={14} />
      </div>
      <div>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--bx-text-dark)',
            marginBottom: 4,
          }}
        >
          {title}
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--bx-text-muted)', lineHeight: 1.5 }}>
          {body}
        </div>
      </div>
    </div>
  );
}
